import React, { useCallback, useContext, useState } from 'react';

import { ColumnWidthOutlined, CompressOutlined } from '@ant-design/icons';
import { Segmented, Space, Tooltip } from 'antd';
import { SegmentedValue } from 'antd/lib/segmented';
import { TableViewsContext } from 'context';
import { cloneDeep, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ViewId, ViewsColDefProps } from 'types/views';

import ViewsConfigurationModal from './ViewsConfigurationModal/ViewsConfigurationModal';

const StyledSegmented = styled(Segmented)`
  & .ant-segmented-item {
    background-color: ${(p) => p.theme.dark_blue_background};
    color: ${(p) => p.theme.white};
  }

  & .ant-segmented-item-selected {
    background-color: ${(p) => p.theme.white};
    color: ${(p) => p.theme.black};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  border-radius: 4px;
  overflow: hidden;
`;

const Separator = styled.div`
  width: 1px;
  height: 14px;
  background-color: ${(p) => p.theme.black}25;
  align-self: center;
  flex-shrink: 0;
`;

const GroupButton = styled.button`
  background-color: ${(p) => p.theme.primary_yellow};
  color: ${(p) => p.theme.black};
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  height: 24px;

  &:hover {
    background-color: ${(p) => p.theme.pale_yellow};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Views: React.FC<ViewsColDefProps> = ({ flatColDefs, separatedColDefs, suppressColMovingForGrids, gridRef }) => {
  const { t } = useTranslation('translation');
  const [isViewsConfigurationModalOpen, setIsViewsConfigurationModalOpen] = useState(false);

  const { selectedViewId, setSelectedViewId, clearSelectedViewId, views, getView, upsertView } =
    useContext(TableViewsContext);

  const saveAllColumnWidths = useCallback(() => {
    const view = getView();
    const columnApi = gridRef?.current?.columnApi;
    if (!view || !columnApi) return;

    const viewCopy = cloneDeep(view);
    const columns = columnApi.getAllColumns();

    columns?.forEach((col) => {
      const colId = col.getColId();
      const width = col.getActualWidth();
      const viewCol = viewCopy.cols.find((c) => c.field === colId);
      if (viewCol && width) {
        viewCol.width = width;
      }
    });

    upsertView(viewCopy);
  }, [getView, gridRef, upsertView]);

  const handleSizeToFit = useCallback(() => {
    gridRef?.current?.api?.sizeColumnsToFit();
    setTimeout(() => saveAllColumnWidths(), 100);
  }, [gridRef, saveAllColumnWidths]);

  const handleAutoSizeAll = useCallback(() => {
    gridRef?.current?.columnApi?.autoSizeAllColumns(true);
    setTimeout(() => saveAllColumnWidths(), 100);
  }, [gridRef, saveAllColumnWidths]);

  const handleViewChange = useCallback(
    (viewId: SegmentedValue) => {
      setSelectedViewId(viewId as ViewId);
    },
    [setSelectedViewId],
  );

  const handleViewClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const target = e.target as HTMLDivElement;
      const clickedViewName = target.textContent;

      const currentView = getView();

      if (clickedViewName === currentView?.name) {
        clearSelectedViewId();
        e.preventDefault();
      }
    },
    [clearSelectedViewId, getView],
  );

  return (
    <>
      <Space>
        <ButtonGroup>
          <GroupButton onClick={() => setIsViewsConfigurationModalOpen(true)}>
            {t('storeFleet.select_columns')}
          </GroupButton>
          {gridRef && (
            <>
              <Separator />
              <Tooltip title={t('storeFleet.auto_size_columns')}>
                <GroupButton onClick={handleAutoSizeAll}>
                  <ColumnWidthOutlined />
                </GroupButton>
              </Tooltip>
              <Separator />
              <Tooltip title={t('storeFleet.size_to_fit')}>
                <GroupButton onClick={handleSizeToFit}>
                  <CompressOutlined />
                </GroupButton>
              </Tooltip>
            </>
          )}
        </ButtonGroup>

        {!isEmpty(views) && (
          <StyledSegmented
            value={selectedViewId || ''}
            options={views.map((view) => ({ value: view.id, label: view.name }))}
            onChange={handleViewChange}
            onClick={handleViewClick}
            size="small"
          />
        )}
      </Space>

      <ViewsConfigurationModal
        isOpen={isViewsConfigurationModalOpen}
        close={() => setIsViewsConfigurationModalOpen(false)}
        flatColDefs={flatColDefs}
        separatedColDefs={separatedColDefs}
        suppressColMovingForGrids={suppressColMovingForGrids}
      />
    </>
  );
};

export default Views;
